import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Download, Printer, ExternalLink } from "lucide-react";
import AdminSettings from "./AdminSettings";
import AdminUsers from "./AdminUsers";
import AdminUserDetail from "./AdminUserDetail";
import AdminWorkshops from "./AdminWorkshops";
import AdminPayouts from "./AdminPayouts";
import AdminLibrary from "./AdminLibrary";
import AdminMockups from "./AdminMockups";
import JobTicket from "./JobTicket";

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-neutral-200 p-6">
      <div className="overline text-neutral-500">{label}</div>
      <div className="font-serif text-4xl mt-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

function ClickCard({ to, navigate, ...props }) {
  return (
    <div onClick={() => navigate(to)} className="cursor-pointer hover:bg-neutral-50 transition-colors" role="button" tabIndex={0}>
      <StatCard {...props} />
    </div>
  );
}

function Overview() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState("month"); // 'week' | 'month' | 'year' | 'all'

  const loadStats = () => {
    const now = new Date();
    let from = null;
    if (period === "week") {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      from = d.toISOString();
    } else if (period === "month") {
      from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    } else if (period === "year") {
      from = new Date(now.getFullYear(), 0, 1).toISOString();
    }
    const params = from ? { from_date: from } : {};
    api.get("/stats/admin", { params }).then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { loadStats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [period]);
  if (!stats) return <div className="overline text-neutral-400">Loading...</div>;

  const periods = [
    { key: "week", label: "Última semana" },
    { key: "month", label: "Este mes" },
    { key: "year", label: "Este año" },
    { key: "all", label: "Todo" },
  ];

  return (
    <div data-testid="admin-overview">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <h1 className="font-serif text-4xl font-light tracking-tight">{t.admin.overview}</h1>
        <div className="flex gap-1" data-testid="period-filter">
          {periods.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`overline text-xs px-3 py-2 border ${period === p.key ? "bg-black text-white border-black" : "border-neutral-300 hover:bg-neutral-50"}`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ClickCard navigate={navigate} to="/admin/orders" label={t.admin.revenue} value={`€${stats.revenue.toFixed(2)}`} sub={`${stats.orders_count} pedidos · ${stats.pending_orders} pendientes`} />
        <ClickCard navigate={navigate} to="/admin/payouts" label="Pendiente artistas" value={`€${stats.artist_pending.toFixed(2)}`} sub={`Pagado: €${stats.artist_paid.toFixed(2)}`} />
        <ClickCard navigate={navigate} to="/admin/settings" label={t.admin.galleryEarnings} value={`€${stats.gallery_earnings.toFixed(2)}`} sub="Comisión galería" />
        <ClickCard navigate={navigate} to="/admin/payouts" label={t.admin.referrerPayouts} value={`€${stats.referrer_payouts.toFixed(2)}`} sub="Comisión 10% referidos" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <ClickCard navigate={navigate} to="/admin/orders" label="Coste impresión" value={`€${stats.print_costs.toFixed(2)}`} sub="Producción + logística" />
        <ClickCard navigate={navigate} to="/admin/orders" label={t.admin.orders} value={stats.orders_count} sub={`${stats.pending_orders} pendientes`} />
        <ClickCard navigate={navigate} to="/admin/approvals" label="Aprobaciones" value={stats.pending_works} sub={`${stats.approved_works} aprobadas · ${stats.total_works} totales`} />
        <ClickCard navigate={navigate} to="/admin/users" label={t.admin.users} value={stats.users_count} sub={`${stats.artists_count} artistas · ${stats.customers_count} clientes · ${stats.referrers_count} referidores`} />
      </div>
    </div>
  );
}

function Approvals() {
  const { t } = useI18n();
  const [pending, setPending] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [rejectFor, setRejectFor] = useState(null); // artwork being rejected
  const [reasonCode, setReasonCode] = useState("");
  const [customMsg, setCustomMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/artworks/pending").then((r) => setPending(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/rejection-reasons").then((r) => setReasons(r.data)).catch(() => {});
  }, []);

  const act = async (id, action) => {
    try { await api.patch(`/artworks/${id}/${action}`); toast.success(action === "approve" ? "Obra aprobada" : "Obra rechazada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openReject = (w) => { setRejectFor(w); setReasonCode(""); setCustomMsg(""); };
  const closeReject = () => { setRejectFor(null); setReasonCode(""); setCustomMsg(""); };

  const submitReject = async () => {
    if (!reasonCode && !customMsg.trim()) { toast.error("Selecciona un motivo o escribe una nota"); return; }
    setSubmitting(true);
    try {
      await api.patch(`/artworks/${rejectFor.id}/reject`, { reason_code: reasonCode, custom_message: customMsg });
      toast.success("Rechazo enviado al artista");
      closeReject();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="admin-approvals">
      <h1 className="font-serif text-4xl font-light tracking-tight mb-8">{t.admin.approvals}</h1>
      {pending.length === 0 ? (
        <div className="overline text-neutral-400 py-16 text-center bg-white border border-neutral-200">—</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pending.map((w) => {
            const sizes = w.sizes || [];
            const retails = sizes.map((s) => s.price || 0);
            const minPrice = retails.length ? Math.min(...retails) : 0;
            const maxPrice = retails.length ? Math.max(...retails) : 0;
            return (
            <div key={w.id} className="bg-white border border-neutral-200" data-testid={`pending-${w.id}`}>
              <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                <img src={fileUrl(w.image_path)} alt={w.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <div className="overline text-neutral-500">{w.category}</div>
                <h3 className="font-serif text-xl mt-1">{w.title}</h3>
                <div className="text-xs text-neutral-500 mt-1">
                  {w.artist_name} · {retails.length > 1 ? `€${minPrice.toFixed(2)} – €${maxPrice.toFixed(2)}` : `€${minPrice.toFixed(2)}`}
                </div>
                {sizes.length > 0 && (
                  <div className="text-xs text-neutral-500 mt-2">
                    {sizes.map((s) => `${s.label} ${s.width_cm}×${s.height_cm}cm`).join(" · ")}
                  </div>
                )}
                <p className="text-sm text-neutral-600 mt-3 line-clamp-2">{w.description}</p>
                <div className="mt-5 grid grid-cols-2 gap-2">
                  <button onClick={() => act(w.id, "approve")} className="bg-black text-white py-2 overline hover:bg-neutral-800" data-testid={`approve-${w.id}`}>{t.admin.approve}</button>
                  <button onClick={() => openReject(w)} className="border border-black py-2 overline hover:bg-neutral-100" data-testid={`reject-${w.id}`}>{t.admin.reject}</button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {rejectFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeReject} data-testid="reject-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full p-8">
            <h2 className="font-serif text-2xl mb-2">Rechazar «{rejectFor.title}»</h2>
            <p className="text-sm text-neutral-500 mb-6">El artista recibirá una notificación + email con el motivo. Puedes seleccionar una razón estándar y añadir una nota personalizada.</p>
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {reasons.map((r) => (
                <label key={r.code} className={`block border px-4 py-3 cursor-pointer transition-colors ${reasonCode === r.code ? "border-black bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`} data-testid={`reason-${r.code}`}>
                  <input type="radio" checked={reasonCode === r.code} onChange={() => setReasonCode(r.code)} className="mr-2" />
                  <span className="text-sm">{r.text}</span>
                </label>
              ))}
            </div>
            <textarea
              placeholder="Nota personalizada (opcional)..."
              value={customMsg}
              onChange={(e) => setCustomMsg(e.target.value)}
              className="w-full border border-neutral-300 px-3 py-2 outline-none focus:border-black bg-white text-sm h-24 resize-none"
              data-testid="reject-custom"
            />
            <div className="mt-6 flex gap-3">
              <button onClick={submitReject} disabled={submitting} className="bg-black text-white px-8 py-3 overline hover:bg-neutral-800 disabled:opacity-50" data-testid="reject-submit">
                {submitting ? "..." : "Enviar rechazo"}
              </button>
              <button onClick={closeReject} className="border border-neutral-300 px-8 py-3 overline hover:bg-neutral-100" data-testid="reject-cancel">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Orders() {
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/orders/${id}/status`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const statusOptions = ["pending", "printing", "shipped", "delivered", "cancelled"];

  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif text-4xl font-light tracking-tight mb-8">{t.admin.orders}</h1>
      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <table className="brutal-table w-full">
          <thead>
            <tr>
              <th>N°</th>
              <th>Cliente</th>
              <th>Items · descargar · ticket</th>
              <th>Total</th>
              <th>{t.admin.orderStatus}</th>
              <th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-${o.id}`}>
                <td className="font-mono text-xs">{o.id.slice(0, 8)}</td>
                <td>
                  <div>{o.shipping_name}</div>
                  <div className="text-xs text-neutral-500">{o.shipping_email}</div>
                  <div className="text-xs text-neutral-500">{o.shipping_city}, {o.shipping_country}</div>
                </td>
                <td>
                  <div className="space-y-2">
                    {o.items.map((i, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs" data-testid={`order-${o.id}-item-${idx}`}>
                        <img src={fileUrl(i.image_path, "md")} alt="" className="w-10 h-10 object-cover bg-neutral-100 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{i.title} × {i.quantity}</div>
                          <div className="text-neutral-500">{i.size_label} · {i.material_name || "—"}</div>
                        </div>
                        <a href={fileUrl(i.image_path, "orig")} download={`${i.title}-original`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 overline text-[10px] px-2 py-1 border border-neutral-300 hover:bg-neutral-50 whitespace-nowrap" data-testid={`download-${o.id}-${idx}`}>
                          <Download size={11} /> Original
                        </a>
                        <Link to={`/admin/ticket/${o.id}/${idx}`} target="_blank" className="inline-flex items-center gap-1 overline text-[10px] px-2 py-1 border border-black bg-neutral-900 text-white hover:bg-neutral-700 whitespace-nowrap" data-testid={`ticket-${o.id}-${idx}`}>
                          <Printer size={11} /> Ticket
                        </Link>
                      </div>
                    ))}
                  </div>
                </td>
                <td className="font-serif text-base">€{o.total.toFixed(2)}</td>
                <td>
                  <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="border border-neutral-300 px-2 py-1 text-xs bg-white" data-testid={`status-select-${o.id}`}>
                    {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan="6" className="text-center py-12 overline text-neutral-400">—</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (loading) return <div className="min-h-screen flex items-center justify-center overline text-neutral-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const links = [
    { to: "/admin", end: true, label: t.admin.overview, key: "overview" },
    { to: "/admin/approvals", label: t.admin.approvals, key: "approvals" },
    { to: "/admin/orders", label: t.admin.orders, key: "orders" },
    { to: "/admin/payouts", label: "Liquidaciones", key: "payouts" },
    { to: "/admin/library", label: "Biblioteca", key: "library" },
    { to: "/admin/users", label: t.admin.users, key: "users" },
    { to: "/admin/workshops", label: "Talleres", key: "workshops" },
    { to: "/admin/mockups", label: "Mockups", key: "mockups" },
    { to: "/admin/settings", label: t.admin.settings, key: "settings" },
  ];

  return (
    <Routes>
      <Route path="ticket/:order_id/:item_index" element={<JobTicket />} />
      <Route element={<DashboardLayout role="admin" links={links} />}>
        <Route index element={<Overview />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="orders" element={<Orders />} />
        <Route path="payouts" element={<AdminPayouts />} />
        <Route path="library" element={<AdminLibrary />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:user_id" element={<AdminUserDetail />} />
        <Route path="workshops" element={<AdminWorkshops />} />
        <Route path="mockups" element={<AdminMockups />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
    </Routes>
  );
}
